import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { StatusPill, type StatusTone } from '@/components/app';
import type { PromoCode } from '../../../api/payments';
import { PromoCodesTableProps } from '../types';

/** Фактическое состояние промокода: флаг «активен» + срок + лимит. */
function promoState(promoCode: PromoCode, now: Date): { label: string; tone: StatusTone } {
  const expired = !!promoCode.valid_until && new Date(promoCode.valid_until) < now;
  const notYetValid = !!promoCode.valid_from && new Date(promoCode.valid_from) > now;
  const usageLimitReached = promoCode.max_uses !== null && promoCode.used_count >= promoCode.max_uses;
  if (promoCode.is_active && !expired && !notYetValid && !usageLimitReached) return { label: 'Активен', tone: 'pos' };
  if (expired) return { label: 'Истёк', tone: 'neg' };
  if (notYetValid) return { label: 'Ещё не начат', tone: 'warn' };
  if (usageLimitReached) return { label: 'Лимит исчерпан', tone: 'neutral' };
  return { label: 'Неактивен', tone: 'neutral' };
}

export function PromoCodesTable({ promoCodes, selectedId, onEdit, onDelete, formatDate, formatValue }: PromoCodesTableProps) {
  const now = new Date();
  return (
    <Table className="min-w-[820px]">
      <TableHeader>
        <TableRow>
          <TableHead>Код</TableHead>
          <TableHead>Тип</TableHead>
          <TableHead className="text-right">Значение</TableHead>
          <TableHead className="text-right">Использовано</TableHead>
          <TableHead>Период</TableHead>
          <TableHead>Статус</TableHead>
          <TableHead className="text-right">
            <span className="sr-only">Действия</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {promoCodes.map((promoCode) => {
          const state = promoState(promoCode, now);
          return (
            <TableRow key={promoCode.id} selected={selectedId === promoCode.id}>
              <TableCell>
                <code className="rounded-lg bg-muted px-2 py-[3px] font-mono text-xs font-semibold">{promoCode.code}</code>
              </TableCell>
              <TableCell>
                <Badge variant="outline">{promoCode.type === 'percent' ? 'Процент' : 'Фикс. сумма'}</Badge>
              </TableCell>
              <TableCell className="whitespace-nowrap text-right font-display font-bold tabular-nums">
                {formatValue(promoCode.type, promoCode.value)}
              </TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums">
                {promoCode.used_count}
                <span className="text-text-2"> / {promoCode.max_uses !== null ? promoCode.max_uses : '∞'}</span>
              </TableCell>
              <TableCell className="whitespace-nowrap text-[13px] tabular-nums text-text-2">
                {formatDate(promoCode.valid_from)} – {formatDate(promoCode.valid_until)}
              </TableCell>
              <TableCell>
                <StatusPill tone={state.tone} dot>
                  {state.label}
                </StatusPill>
              </TableCell>
              <TableCell className="text-right">
                <div className="inline-flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="icon-sm"
                    aria-label={`Изменить промокод ${promoCode.code}`}
                    onClick={() => onEdit(promoCode)}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Удалить промокод ${promoCode.code}`}
                    onClick={() => onDelete(promoCode.id)}
                    className="text-text-2 hover:bg-neg-soft hover:text-neg"
                  >
                    <Trash2 />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export function PromoCodesTableSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="flex flex-col" aria-busy="true" aria-label="Загрузка промокодов">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-6 border-t border-border-soft px-[18px] py-3.5">
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="ml-auto h-4 w-32" />
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
      ))}
    </div>
  );
}
